import { Fragment, useState } from "react";
import { monthsText } from "../../lib/duration";
import type { AssetCard, DecisionRequest, InsightDocument, RegisterRow, VendorRow, WaveRow } from "../../api/insights";
import { AssetCardView, PriorityChip, windowLabel } from "./views";
import type { BacklogSummary } from "../../api/remediation";
import { moduleShortLabel, riskLevelColor } from "../../lib/riskVocabulary";
import { LevelBadge, Likert, NAVY, costBandText, costRange, ownerDisplay, shortDate, waveDateLabel } from "./primitives";

/** Data panels of the cockpit: decisions, waves, vendors, crypto, compliance, coverage, register. */

const DECIDER_COLOR: Record<DecisionRequest["decider"], string> = {
  "Board / ExCo": NAVY,
  CISO: "#7c3aed",
  Procurement: "#0f766e",
};

export function DecisionsPanel({ decisions }: { decisions: DecisionRequest[] }) {
  if (decisions.length === 0) return <p className="text-xs text-ink-500">No decisions outstanding.</p>;
  // The engine's order: by score (severity × systems affected × urgency), the order the reports use.
  const ordered = decisions;
  return (
    <ol className="space-y-2" data-testid="decisions">
      {ordered.map((d, i) => (
        <li key={d.id} className="flex gap-3 items-start" title={d.why}>
          <span className="text-xs font-semibold tabular-nums text-ink-400 w-4 mt-0.5">{i + 1}</span>
          <span className="flex-1 text-sm text-ink-900 leading-snug">{d.ask}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm whitespace-nowrap" style={{ color: DECIDER_COLOR[d.decider], background: `${DECIDER_COLOR[d.decider]}12` }}>{d.decider}</span>
        </li>
      ))}
    </ol>
  );
}

function ProgressBar({ done, open, overdue, total }: { done: number; open: number; overdue: number; total: number }) {
  if (total === 0) return <div className="h-1.5 rounded-full bg-ink-50" />;
  const w = (n: number) => `${(n / total) * 100}%`;
  return (
    <div className="h-1.5 rounded-full bg-ink-50 flex overflow-hidden" aria-label={`${done} of ${total} done`}>
      <div style={{ width: w(done), background: riskLevelColor("Very_low") }} />
      <div style={{ width: w(overdue), background: riskLevelColor("Critical") }} />
      <div style={{ width: w(Math.max(0, open - overdue)), background: "#cbd5e1" }} />
    </div>
  );
}

export function WavePlan({ waves, backlog, currency }: { waves: WaveRow[]; backlog: BacklogSummary | undefined; currency: string | null }) {
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="wave-plan">
      {waves.map((w) => {
        const b = backlog?.by_wave.find((x) => x.wave === w.wave);
        return (
          <div key={w.wave} className="border border-ink-100 rounded-sm p-3 bg-white" data-testid={`wave-${w.wave}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: w.wave === 1 ? riskLevelColor("Critical") : NAVY }}>Group {w.wave}</span>
              {(() => { const d = waveDateLabel(w); return <span className="text-[11px] text-ink-500 text-right" data-testid={`wave-${w.wave}-dates`}>{d.start}{d.complete && <><br />{d.complete}</>}</span>; })()}
            </div>
            <div className="text-sm font-medium text-ink-900 mt-0.5">{w.label}</div>
            <div className="flex items-baseline gap-4 mt-2">
              <div><span className="text-2xl font-semibold tabular-nums" style={{ color: NAVY }}>{w.assets}</span> <span className="text-[11px] text-ink-500">assets</span></div>
              {w.high_plus > 0 && <div className="text-[11px]" style={{ color: riskLevelColor("Very_high") }}>{w.high_plus} High+</div>}
              <div className="text-[11px] text-ink-600 ml-auto" title={`${w.cost.assets_costed} of ${w.assets} assets have a cost estimate`}>
                {w.cost.assets_costed > 0 ? costRange(w.cost.low, w.cost.open_ended > 0 ? null : w.cost.high, currency) : "cost not estimated"}
              </div>
            </div>
            {b && (
              <div className="mt-2.5">
                <ProgressBar done={b.done} open={b.open} overdue={b.overdue} total={b.total} />
                <div className="flex gap-3 text-[10.5px] text-ink-500 mt-1">
                  <span>{b.done}/{b.total} actions closed</span>
                  {b.overdue > 0 && <span style={{ color: riskLevelColor("Critical") }}>{b.overdue} overdue</span>}
                  {b.unassigned > 0 && <span>{b.unassigned} unassigned</span>}
                </div>
              </div>
            )}
            {w.top_actions.length > 0 && (
              <ul className="mt-2 text-[11px] text-ink-600 space-y-0.5">
                {w.top_actions.slice(0, 3).map((a) => <li key={a.code} className="truncate" title={a.title}>· {a.title} <span className="text-ink-400">({a.assets})</span></li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const COMMITMENT_LABEL: Record<string, string> = { none: "None", verbal: "Verbal only", written_roadmap: "Written roadmap", delivered: "Delivered" };

function commitmentCell(c: VendorRow["commitment"]) {
  if (!c || !c.state) return <span className="text-ink-400">Not recorded</span>;
  const bad = c.state === "none" || c.state === "verbal";
  return (
    <span style={{ color: c.state === "none" ? riskLevelColor("Critical") : bad ? riskLevelColor("High") : c.state === "delivered" ? riskLevelColor("Very_low") : undefined }}>
      {COMMITMENT_LABEL[c.state] ?? c.state}{c.committed_date && c.state === "written_roadmap" ? ` · ${shortDate(c.committed_date)}` : ""}
    </span>
  );
}

export function VendorTable({ vendors, limit = 8 }: { vendors: InsightDocument["vendors"]; limit?: number }) {
  if (vendors.rows.length === 0) return <p className="text-xs text-ink-500">No vendor-supplied assets recorded. Add vendor details to assets to see supply-chain exposure.</p>;
  // Riskiest first: blocking, then Priority 1 assets, then High+ assets, so a limit never hides a risky vendor (C-013).
  const sorted = vendors.rows.slice().sort((a, b) => Number(b.blocking) - Number(a.blocking) || b.p1_assets - a.p1_assets || b.high_plus_assets - a.high_plus_assets || b.assets - a.assets || a.vendor.localeCompare(b.vendor));
  const rows = sorted.slice(0, limit);
  const n = (k: number, one: string, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;
  const blockingN = vendors.rows.filter((v) => v.blocking).length;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="vendor-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-ink-500 text-left">
            <th className="py-1.5 pr-2">Vendor</th>
            <th className="py-1.5 px-2 text-right">Assets</th>
            <th className="py-1.5 px-2">Commitment</th>
            <th className="py-1.5 px-2">Readiness</th>
            <th className="py-1.5 px-2">Engagement</th>
            <th className="py-1.5 px-2">Switching</th>
            <th className="py-1.5 pl-2">Questionnaire</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v: VendorRow) => (
            <tr key={v.vendor} className="border-t border-ink-50">
              <td className="py-1.5 pr-2 font-medium text-ink-900 whitespace-nowrap">
                {v.blocking && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: riskLevelColor("Critical") }} title="Blocks migration" />}
                {v.vendor}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">{v.assets}{v.p1_assets > 0 ? <span className="text-[10px] ml-1" style={{ color: riskLevelColor("Critical") }}>({v.p1_assets} P1)</span> : v.high_plus_assets > 0 && <span className="text-[10px] ml-1" style={{ color: riskLevelColor("Very_high") }}>({v.high_plus_assets} High+)</span>}</td>
              <td className="py-1.5 px-2 whitespace-nowrap">{commitmentCell(v.commitment)}</td>
              <td className="py-1.5 px-2"><Likert value={v.readiness} goodWhen="high" /></td>
              <td className="py-1.5 px-2"><Likert value={v.engagement} goodWhen="high" /></td>
              <td className="py-1.5 px-2"><Likert value={v.sourcing_difficulty} goodWhen="low" /></td>
              <td className="py-1.5 pl-2 text-ink-600">{v.questionnaire ? `${v.questionnaire.state.replace(/_/g, " ")} (cycle ${v.questionnaire.cycle})` : <span style={{ color: riskLevelColor("High") }}>Not sent</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-ink-500 mt-2">
        {n(vendors.rows.length, "vendor")} supplying {n(vendors.vendor_supplied_assets, "asset")} · {n(blockingN, "vendor")} {blockingN === 1 ? "blocks" : "block"} migration · {n(vendors.without_questionnaire, "vendor")} without a readiness questionnaire
        {vendors.rows.length > limit && ` · ${n(vendors.rows.length - limit, "lower-risk vendor")} not shown`}
      </p>
    </div>
  );
}

function StackBar({ parts }: { parts: Array<{ n: number; color: string; label: string }> }) {
  const total = parts.reduce((a, p) => a + p.n, 0);
  if (total === 0) return <div className="h-2.5 bg-ink-50 rounded-sm" />;
  return (
    <div className="h-2.5 flex rounded-sm overflow-hidden">
      {parts.filter((p) => p.n > 0).map((p) => <div key={p.label} style={{ width: `${(p.n / total) * 100}%`, background: p.color }} title={`${p.label}: ${p.n}`} />)}
    </div>
  );
}

const VULN = riskLevelColor("Critical"), WEAK = riskLevelColor("Moderate"), SAFE = riskLevelColor("Very_low"), UNK = "#cbd5e1";

export function CryptoPosture({ crypto }: { crypto: InsightDocument["crypto"] }) {
  const t = crypto.totals;
  if (t.artifacts === 0) return <p className="text-xs text-ink-500">No cryptographic inventory yet. Import a CBOM or scan results to see algorithms, protocols, libraries and certificates.</p>;
  return (
    <div className="space-y-4" data-testid="crypto-posture">
      <div>
        <div className="flex justify-between text-[11px] text-ink-500 mb-1">
          <span>{t.artifacts} artifacts across {crypto.assets_inventoried} of {crypto.assets_total} assets</span>
          <span><span style={{ color: VULN }}>{t.vulnerable} vulnerable</span> · {t.weakened} weakened · {t.resistant} quantum-safe</span>
        </div>
        <StackBar parts={[{ n: t.vulnerable, color: VULN, label: "Vulnerable" }, { n: t.weakened, color: WEAK, label: "Weakened" }, { n: t.resistant, color: SAFE, label: "Quantum-safe" }, { n: t.unknown, color: UNK, label: "Unknown" }]} />
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">Algorithms</h4>
          <ul className="space-y-1.5">
            {crypto.algorithms.slice(0, 6).map((a) => (
              <li key={a.algorithm} className="grid grid-cols-[80px_1fr_44px] items-center gap-2 text-xs">
                <span className="font-medium text-ink-800 truncate">{a.algorithm}</span>
                <StackBar parts={[{ n: a.vulnerable, color: VULN, label: "Vulnerable" }, { n: a.weakened, color: WEAK, label: "Weakened" }, { n: a.resistant, color: SAFE, label: "Quantum-safe" }, { n: a.unknown, color: UNK, label: "Unknown" }]} />
                <span className="text-right text-ink-500 tabular-nums">{a.assets} ast</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">Protocols</h4>
          {crypto.protocols.length === 0 ? <p className="text-xs text-ink-400">None recorded.</p> : (
            <ul className="space-y-1">
              {crypto.protocols.slice(0, 6).map((p) => (
                <li key={p.protocol} className="flex justify-between text-xs">
                  <span className="text-ink-800">{p.protocol}</span>
                  <span style={{ color: /vuln/i.test(p.vulnerability) ? VULN : /weak/i.test(p.vulnerability) ? WEAK : /resist|safe/i.test(p.vulnerability) ? SAFE : "#64748b" }}>{p.vulnerability.replace(/_/g, " ")} · {p.assets} ast</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">Libraries</h4>
          {crypto.libraries.length === 0 ? <p className="text-xs text-ink-400">None recorded.</p> : (
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] text-ink-400 text-left"><th className="font-normal">Library</th><th className="font-normal text-center">PQC</th><th className="font-normal text-center">FIPS</th><th className="font-normal text-right">Assets</th></tr></thead>
              <tbody>
                {crypto.libraries.slice(0, 6).map((l) => (
                  <tr key={`${l.name}@${l.version}`}>
                    <td className="py-0.5 text-ink-800">{l.name}{l.version && <span className="text-ink-400"> {l.version}</span>}</td>
                    <td className="text-center">{l.pqc_support === null ? "—" : l.pqc_support ? <span style={{ color: SAFE }}>✓</span> : <span style={{ color: VULN }}>✗</span>}</td>
                    <td className="text-center">{l.fips_validated === null ? "—" : l.fips_validated ? <span style={{ color: SAFE }}>✓</span> : <span className="text-ink-400">✗</span>}</td>
                    <td className="text-right tabular-nums">{l.assets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">Certificates</h4>
          {crypto.certificates.total === 0 ? <p className="text-xs text-ink-400">None recorded.</p> : (
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { n: crypto.certificates.vulnerable, l: "quantum-vulnerable", c: VULN },
                { n: crypto.certificates.expiring_12m, l: "expire ≤ 12 m", c: riskLevelColor("High") },
                { n: crypto.certificates.expiring_24m, l: "expire ≤ 24 m", c: NAVY },
              ].map((x) => (
                <div key={x.l}><div className="text-lg font-semibold tabular-nums" style={{ color: x.n > 0 ? x.c : "#94a3b8" }}>{x.n}</div><div className="text-[10px] text-ink-500 leading-tight">{x.l}</div></div>
              ))}
              <div className="col-span-3 text-[10.5px] text-ink-400">of {crypto.certificates.total} certificates · renewals are the cheapest migration window</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CompliancePanel({ doc }: { doc: InsightDocument }) {
  const c = doc.compliance;
  const total = doc.kpis.inventory_coverage.assets_total;
  // Today's rules and the post-quantum target are reported separately (C-111). The Compliance
  // dimension score still averages both; this panel only separates what each says.
  const groups: Array<{ key: "current" | "target"; title: string; help: string }> = [
    { key: "current", title: "Today's rules", help: "Rules in force today: a shortfall is a gap now." },
    { key: "target", title: "Post-quantum target", help: "Quantum-safe target policies: the distance still to cover by the deadlines." },
  ];
  const inGroup = (h: "current" | "target" | null, key: "current" | "target") => (key === "target" ? h === "target" : h !== "target");
  return (
    <div data-testid="compliance-panel">
      <div className="text-[11px] text-ink-500 mb-2">Assessed {c.assets_assessed} of {total} assets{c.assets_assessed < total && " — the rest need a crypto inventory"}</div>
      {c.policies.length === 0 ? <p className="text-xs text-ink-500">No policies assigned to this project. Assign NIST IR 8547, NIST FIPS 203/204/205, ENISA / ECCG, CNSA 2.0 or another bundled policy on the Compliance tab.</p> : (
        <div className="space-y-4">
          {groups.map((g) => {
            const rows = c.policies.filter((p) => inGroup(p.horizon, g.key));
            const h = c.by_horizon?.[g.key];
            return (
              <section key={g.key} data-testid={`compliance-${g.key}`}>
                <div className="text-xs font-semibold text-ink-800" title={g.help}>
                  {g.title}: {h && h.assets_evaluated > 0
                    ? <span className="tabular-nums">{h.assets_short} of {h.assets_evaluated} assets fall short</span>
                    : <span className="font-normal text-ink-500">no policy of this kind assigned</span>}
                </div>
                <div className="text-[11px] text-ink-500 mb-1">{g.help}</div>
                {rows.length > 0 && (
                  <ul className="space-y-2">
                    {rows.map((p) => {
                      const okN = p.assets_evaluated - p.assets_non_compliant;
                      return (
                        <li key={p.short_code}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium text-ink-800">{p.name}{p.horizon === null && <span className="font-normal text-ink-400"> · not classified</span>}</span>
                            <span className="tabular-nums"><span style={{ color: p.assets_non_compliant > 0 ? VULN : SAFE }}>{p.assets_non_compliant} non-compliant</span><span className="text-ink-400"> / {p.assets_evaluated} · {p.violations} violations</span></span>
                          </div>
                          <StackBar parts={[{ n: p.assets_non_compliant, color: VULN, label: "Non-compliant" }, { n: okN, color: SAFE, label: "Compliant" }, { n: Math.max(0, total - p.assets_evaluated), color: "#eef0f3", label: "Not assessed" }]} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
          <p className="text-[11px] text-ink-400">The Compliance dimension score averages both kinds of policy.</p>
        </div>
      )}
    </div>
  );
}

function CoverageRow({ label, n, of, hint }: { label: string; n: number; of: number; hint?: string }) {
  const p = of > 0 ? Math.round((n / of) * 100) : 0;
  const c = p >= 80 ? SAFE : p >= 50 ? WEAK : VULN;
  return (
    <li title={hint}>
      <div className="flex justify-between text-xs mb-0.5"><span className="text-ink-700">{label}</span><span className="tabular-nums text-ink-500">{n} / {of}</span></div>
      <div className="h-1.5 bg-ink-50 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${p}%`, background: c }} /></div>
    </li>
  );
}

/** How much of the picture is evidence: inventory, threat scenarios, compliance, inputs, owners. */
export function CoveragePanel({ doc }: { doc: InsightDocument }) {
  const k = doc.kpis;
  const dq = doc.data_quality;
  return (
    <div data-testid="coverage-panel">
      <ul className="space-y-2.5">
        <CoverageRow label="Crypto inventory (CBOM)" n={k.inventory_coverage.assets_inventoried} of={k.inventory_coverage.assets_total} />
        <CoverageRow label="Quantum threat scenarios modelled" n={doc.threat_coverage.assets_with_pqc_scenarios} of={doc.threat_coverage.assets_scored} hint="Assets whose type has post-quantum threat compositions; the rest use generic cyber scenarios." />
        <CoverageRow label="Policy compliance assessed" n={k.inventory_coverage.compliance_assessed} of={k.inventory_coverage.assets_total} />
        <CoverageRow label="Scoring inputs evidenced" n={k.evidence_coverage.evidenced} of={k.evidence_coverage.total} hint={`${k.evidence_coverage.defaulted} inputs used defaults`} />
        <CoverageRow label="High-risk assets with a named owner" n={k.named_owners.named} of={k.named_owners.high_plus} />
      </ul>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-500 mt-3">
        {doc.readiness?.overall_avg != null ? <span>Organisational readiness: <strong className="text-ink-800">{doc.readiness.overall_avg.toFixed(1)} of 4</strong> ({doc.readiness.answered} of {doc.readiness.total} answered, as of {new Date(doc.readiness.as_of).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })})</span> : <span>Organisational readiness: not yet assessed</span>}
        {dq.cost_values_outside_bands > 0 && <span style={{ color: riskLevelColor("High") }}>{dq.cost_values_outside_bands} cost estimates outside the configured bands</span>}
        {dq.unresolved_artifacts > 0 && <span>{dq.unresolved_artifacts} artifact{dq.unresolved_artifacts === 1 ? "" : "s"} not matched to the knowledge base</span>}
      </div>
      {(dq.profile_notes ?? []).map((t) => <p key={t} className="text-[11px] text-ink-600 mt-2" data-testid="profile-note">{t}</p>)}
    </div>
  );
}

export function DependenciesPanel({ deps }: { deps: InsightDocument["dependencies"] }) {
  if (deps.length === 0) return <p className="text-xs text-ink-500">No shared platforms or libraries recorded.</p>;
  const max = Math.max(...deps.map((d) => d.dependents));
  return (
    <ul className="space-y-1.5" data-testid="dependencies">
      {deps.slice(0, 6).map((d) => (
        <li key={d.asset_id} className="grid grid-cols-[1fr_90px_24px] items-center gap-2 text-xs">
          <span className="truncate text-ink-800">{d.asset_name} <span className="text-ink-400">{d.kind === "library" ? "library" : d.kind === "issuer" ? "issuing CA" : "platform"}</span></span>
          <div className="h-2 bg-ink-50 rounded-sm"><div className="h-full rounded-sm" style={{ width: `${(d.dependents / max) * 100}%`, background: NAVY }} /></div>
          <span className="tabular-nums text-right text-ink-600">{d.dependents}</span>
        </li>
      ))}
    </ul>
  );
}

function windowCell(t: RegisterRow["time"]) {
  const w = windowLabel(t);
  const color = w.tone === "late" ? riskLevelColor("Critical") : w.tone === "tight" ? riskLevelColor("Moderate") : undefined;
  const why = t.decided_by === "lifetime" ? "data lifetime" : t.decided_by === "operational" ? "migration" : null;
  return (
    <span title={`D ${Math.round(t.t1 * 10) / 10} yrs + Y ${t.m === null ? "—" : monthsText(t.m)}${t.s !== null ? ` (+ X ${t.s} yrs)` : ""} vs Z ${t.q_years === null ? "—" : Math.round(t.q_years * 10) / 10} yrs (planning year ${t.q_year ?? "—"}). D: start delay; Y: migration time; X: must stay protected; Z: years left`}>
      <span className={w.tone === "late" ? "font-semibold" : "text-ink-600"} style={{ color }}>{w.text}</span>
      {why && w.tone !== "ok" && <span className="block text-[10px] text-ink-400">{why}</span>}
      {t.regulatory_miss && <span className="block text-[10px]" style={{ color: riskLevelColor("Critical") }}>misses deadline</span>}
    </span>
  );
}

/** Wave and cost are migration planning: shown only when `planning` is set (Migration programme lens, C-018). */
export function RiskRegister({ rows, currency, cards = [], cardFor: shared = {}, limit = 10, planning = false }: { rows: RegisterRow[]; currency: string | null; cards?: AssetCard[]; cardFor?: Record<string, string>; limit?: number; planning?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  if (rows.length === 0) return <p className="text-xs text-ink-500">No assessed assets.</p>;
  const shown = all ? rows : rows.slice(0, limit);
  const own = new Map(cards.map((c) => [c.asset_id, c]));
  // An asset of a group shows the group's card (same type, vendor, findings and time inputs).
  // No asset in view records an owning ROLE, so the column has nothing to say: it is
  // dropped rather than filled with placeholders. A blank owner cell is an invitation
  // to type a person's name into it, which is the thing being removed.
  // Shown when any row has an owner to show: a role, else a named person, else a recorded contact.
  const anyOwnerRole = rows.some((r) => !!ownerDisplay(r.owner));
  const cardFor = new Map(rows.flatMap((r) => { const c = own.get(r.asset_id) ?? (shared[r.asset_id] ? own.get(shared[r.asset_id]!) : undefined); return c ? [[r.asset_id, c] as const] : []; }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="risk-register">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-ink-500 text-left">
            <th className="py-1.5 pr-1 w-6">#</th>
            <th className="py-1.5 px-2">Asset · what the risk is</th>
            <th className="py-1.5 px-1">Priority</th>
            <th className="py-1.5 px-2">Overall</th>
            <th className="py-1.5 px-2">Tech. exposure</th>
            <th className="py-1.5 px-2">Time</th>
            {anyOwnerRole && <th className="py-1.5 px-2">Owner</th>}
            <th className="py-1.5 px-2">Vendor</th>
            <th className="py-1.5 px-2">Next action</th>
            {planning && <th className="py-1.5 px-2">Wave · dates</th>}
            {planning && <th className="py-1.5 pl-2 text-right">Cost</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, idx) => (
            <Fragment key={r.asset_id}>
              <tr className="border-t border-ink-50 hover:bg-ink-50/50 cursor-pointer align-top" onClick={() => setOpen(open === r.asset_id ? null : r.asset_id)}>
                <td className="py-1.5 pr-1 text-ink-400 tabular-nums">{idx + 1}</td>
                <td className="py-1.5 px-2 max-w-[300px]">
                  <div className="font-medium text-ink-900">{r.asset_name} <span className="text-ink-300">{open === r.asset_id ? "▴" : "▾"}</span></div>
                  <div className="text-[10.5px] text-ink-500">{r.summary}</div>
                </td>
                <td className="py-1.5 px-1 whitespace-nowrap"><PriorityChip p={r.priority} />{r.fix_now && <span className="ml-1 inline-block text-[9.5px] font-bold px-1 rounded-sm text-white" style={{ background: riskLevelColor("Critical") }} title="Uses cryptography broken today">Fix now</span>}</td>
                <td className="py-1.5 px-2"><LevelBadge level={r.overall.level} score={r.overall.score} /></td>
                <td className="py-1.5 px-2">{r.exposure_view.provisional ? <span className="text-[11px] text-ink-500" title="Threat only: no cryptographic inventory">Prov. {Math.round(r.exposure_view.score ?? 0)}</span> : <LevelBadge level={r.exposure_view.level} score={r.exposure_view.score} />}</td>
                <td className="py-1.5 px-2 whitespace-nowrap">{windowCell(r.time)}</td>
                {anyOwnerRole && <td className="py-1.5 px-2">{ownerDisplay(r.owner) ? <span className={r.owner.named_person ? "text-ink-800" : "text-ink-500 italic"}>{ownerDisplay(r.owner)}</span> : <span style={{ color: riskLevelColor("High") }}>Unassigned</span>}</td>}
                <td className="py-1.5 px-2 text-ink-600">{r.vendor ?? "—"}</td>
                <td className="py-1.5 px-2 text-ink-800 max-w-[200px]">{r.actions[0]?.title ?? "—"}</td>
                {planning && <td className="py-1.5 px-2 whitespace-nowrap">{r.time.status === "migrated" ? <span className="text-ink-500">Migrated</span> : <><span className="font-semibold" style={{ color: r.wave === 1 ? riskLevelColor("Critical") : NAVY }}>W{r.delivery_wave ?? r.wave}</span> {(() => { const d = waveDateLabel(r); return <span className="text-ink-500" title={d.complete ?? undefined}>{d.start}{d.complete && <span className="block text-[10px]">{d.complete}</span>}</span>; })()}</>}</td>}
                {planning && <td className="py-1.5 pl-2 text-right text-ink-600 whitespace-nowrap">{r.cost ? costBandText(r.cost, currency) : "—"}</td>}
              </tr>
              {open === r.asset_id && (
                <tr className="bg-ink-50/40">
                  <td />
                  <td colSpan={planning ? 10 : 8} className="px-2 py-3">
                    {cardFor.has(r.asset_id) ? <>{cardFor.get(r.asset_id)!.asset_id !== r.asset_id && <p className="text-[11px] text-ink-500 mb-1">Shown for {cardFor.get(r.asset_id)!.asset_name}, which has the same type, vendor, findings and time inputs as this asset.</p>}<AssetCardView card={cardFor.get(r.asset_id)!} /></> : (
                      <div className="text-[11.5px] text-ink-700 space-y-1">
                        {r.risk_statement ? <p><strong>Quantum threat scenario:</strong> {r.risk_statement.threat_event}{r.risk_statement.impact && <> → {r.risk_statement.impact}</>}.</p> : <p className="text-ink-500">No post-quantum threat scenario is modelled for this asset type yet.</p>}
                        <p><strong>Top drivers:</strong> {r.drivers.map((d) => `${moduleShortLabel(d.module_code)} ${Math.round(d.share * 100)}%`).join(" · ")}</p>
                        {r.actions.length > 1 && <p><strong>All actions:</strong> {r.actions.map((a) => a.title).join(" · ")}</p>}
                        <p className="text-ink-500">Baseline {r.baseline.level?.replace(/_/g, " ") ?? "—"} · criticality {r.criticality?.replace(/_/g, " ") ?? "—"} · exposure {r.exposure ?? "—"} · {r.confidence} confidence</p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {rows.length > limit && (
        <button type="button" className="mt-2 text-xs text-accent hover:underline" onClick={() => setAll((a) => !a)}>{all ? `Show top ${limit}` : `Show all ${rows.length}`}</button>
      )}
    </div>
  );
}
